import { z } from 'zod';
import { http } from '@/lib/ats-https';
import { http as oldHttp } from '@/lib/http';

const SEARCH_RESULTS_PER_PAGE = 50;

// Contact search function (moved from old api.ts)
const getContactDetails = async ({
  searchTerm,
  signal,
}: {
  searchTerm: string;
  includeResume: boolean;
  signal: AbortSignal;
}) => {
  const response = await oldHttp.post(
    `/search/circles/contacts`,
    {
      extended_fields: true,
      limit: SEARCH_RESULTS_PER_PAGE,
      name: searchTerm,
      offset: 0,
    },
    { signal }
  );
  console.log('response:', response);
  return response?.data;
};

// Schema for test score request
const _testScoreRequestSchema = z.object({
  contact_id: z.number().optional(),
  result_key: z.string().optional(),
  resume_file: z.any().optional(), // File upload
});

// Schema for session contact
const sessionContactSchema = z.object({
  id: z.number(),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
});

// Schema for session contact resume
const sessionContactResumeSchema = z.object({
  id: z.number(),
  filename: z.string(),
  file_size: z.number(),
});

// Schema for session
const sessionSchema = z.object({
  contact: sessionContactSchema.optional(),
  contact_resume: sessionContactResumeSchema.optional(),
});

// Schema for test score response
const testScoreResponseSchema = z.object({
  data: z.object({
    status: z.enum(['pending', 'completed', 'failed']),
    result_key: z.string(),
    session: sessionSchema.optional(),
  }),
  errors: z.array(z.any()),
});

// Schema for attribute in test scoring (matches the API spec exactly)
const testScoringAttributeSchema = z.object({
  label: z.string(),
  description: z.string(),
  attribute_type: z.string(),
  is_negative: z.boolean().optional(), // Made optional since it's not always present in API response
  is_valid: z.boolean().nullable(),
  reason: z.string().nullable().optional(), // Made optional since it might not be present for all attributes
  valid_type: z.enum(['error', 'warning', 'success']).nullable().optional(), // Made optional since it might not be present for all attributes
  position: z.number(),
  weight: z.union([z.number(), z.string()]).transform((val) => {
    if (typeof val === 'string') {
      const num = parseFloat(val);
      return isNaN(num) ? 0 : num;
    }
    return val;
  }),
  id: z.number().nullable(),
});

// Schema for attribute scores in scoring result (nested within test_scoring)
const scoringResultAttributeScoreSchema = z.object({
  attribute_name: z.string(),
  score_level: z.string(),
  reasoning: z.string(),
});

// Schema for scoring result (nested within test_scoring)
const scoringResultSchema = z.object({
  rating: z.number(),
  score_level: z.string(),
  attribute_scores: z.array(scoringResultAttributeScoreSchema),
});

// Schema for test scoring data (nested within data)
const _testScoringSchema = z.object({
  id: z.string().nullable(),
  status: z.enum(['pending', 'completed', 'failed']),
  attributes: z.array(testScoringAttributeSchema),
  scoring_result: scoringResultSchema.optional(),
});

// Schema for attribute scores in main response (top level)
const attributeScoreSchema = z.object({
  attribute_id: z.number().nullable(),
  attribute_label: z.string(),
  score_level: z.enum(['high', 'avg', 'low']),
  reasoning: z.string(),
  confidence: z.number(),
  score: z.number(),
});

// Schema for test score status response - handles both pending and completed states
const testScoreStatusResponseSchema =
  // Pending state - same structure as initial POST response
  z.object({
    data: z.object({
      status: z.enum(['pending', 'completed', 'failed']),
      result_key: z.string(),
      session: sessionSchema.optional(),
      trace_id: z.string().optional().nullable(),
      overall_score_level: z.enum(['high', 'avg', 'low']).optional(),
      rating: z.number().optional(),
      overall_evaluation: z.string().optional(),
      overall_reasoning: z.string().optional(),
      confidence_score: z.number().optional(),
      attribute_scores: z.array(attributeScoreSchema).optional(),
      attributes: z.array(testScoringAttributeSchema).optional(),
      error: z.string().optional(),
    }),
    errors: z.array(z.any()),
  });

// Schema for delete response
const deleteSessionResponseSchema = z.object({
  message: z.string().optional(),
  errors: z.array(z.any()).optional(),
});

const BASE = '/ats_jobs';

// API functions
async function testScore(
  atsJobId: string,
  payload: z.infer<typeof testScoreRequestSchema>
): Promise<{
  data?: z.infer<typeof testScoreResponseSchema>;
  error?: Error;
}> {
  try {
    const formData = new FormData();

    if (payload.contact_id) {
      formData.append('ai_scoring[contact_id]', payload.contact_id.toString());
    }

    if (payload.result_key) {
      formData.append('ai_scoring[result_key]', payload.result_key);
    }

    if (payload.resume_file) {
      formData.append('ai_scoring[resume_file]', payload.resume_file);
    }

    const response = await http.post(
      `${BASE}/${atsJobId}/ai_scoring/attributes/test_score`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    try {
      const data = testScoreResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log('🔄 Zod validation error testScore:', error);
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

async function getTestScoreStatus(
  atsJobId: string,
  resultKey: string
): Promise<{
  data?: z.infer<typeof testScoreStatusResponseSchema>;
  error?: Error;
}> {
  try {
    const response = await http.get(
      `${BASE}/${atsJobId}/ai_scoring/attributes/test_score/${resultKey}`
    );

    try {
      const data = testScoreStatusResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log('🔄 Zod validation error getTestScoreStatus:', error);
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

async function deleteTestSession(
  atsJobId: string,
  resultKey: string
): Promise<{
  data?: z.infer<typeof deleteSessionResponseSchema>;
  error?: Error;
}> {
  try {
    const response = await http.delete(
      `${BASE}/${atsJobId}/ai_scoring/attributes/test_score/${resultKey}`
    );

    try {
      const data = deleteSessionResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log('🔄 Zod validation error deleteTestSession:', error);
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

// Consolidated API export
export const aiScoreTestApi = {
  testScore,
  getTestScoreStatus,
  deleteTestSession,
  getContactDetails, // Include contact search functionality
};

// Export types
export type TestScoreRequest = z.infer<typeof testScoreRequestSchema>;
export type TestScoreResponse = z.infer<typeof testScoreResponseSchema>;
export type TestScoreStatusResponse = z.infer<
  typeof testScoreStatusResponseSchema
>;
export type AttributeScore = z.infer<typeof attributeScoreSchema>;
export type TestScoring = z.infer<typeof testScoringSchema>;
export type Session = z.infer<typeof sessionSchema>;
export type TestScoringAttribute = z.infer<typeof testScoringAttributeSchema>;
